import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, MessageCircle, Send } from 'lucide-react';
import Header from './components/Header23';
import Footer from './components/Footer23';

const Template23Contact = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        orderType: 'general',
        message: '',
    });
    const [submitted, setSubmitted] = useState(false);

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setSubmitted(true);
    };

    const inputStyle = {
        width: '100%',
        padding: '14px 16px',
        border: '1px solid #E8E0D4',
        borderRadius: '8px',
        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
        fontSize: '14px',
        color: '#1B3A2D',
        backgroundColor: '#FFFDF8',
        outline: 'none',
        transition: 'border-color 0.3s ease',
        boxSizing: 'border-box',
    };

    const labelStyle = {
        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
        fontSize: '13px',
        fontWeight: 600,
        color: '#1B3A2D',
        display: 'block',
        marginBottom: '6px',
    };

    const contactInfo = [
        {
            icon: <MessageCircle size={22} />,
            title: 'WhatsApp for Custom Orders',
            detail: '+91 98765 43210',
            subtitle: 'For custom sizing, bridal consultations & urgent queries',
            action: 'https://wa.me/919876543210',
            actionLabel: 'Chat on WhatsApp',
            highlight: true,
        },
        {
            icon: <Phone size={22} />,
            title: 'Phone',
            detail: '+91 98765 43210',
            subtitle: 'Mon - Sat: 10 AM - 7 PM IST',
            action: 'tel:+919876543210',
            actionLabel: 'Call Now',
            highlight: false,
        },
        {
            icon: <Mail size={22} />,
            title: 'Email',
            detail: 'hello@utsav.com',
            subtitle: 'We respond within 24 hours',
            action: 'mailto:hello@utsav.com',
            actionLabel: 'Send Email',
            highlight: false,
        },
        {
            icon: <MapPin size={22} />,
            title: 'Studio',
            detail: 'Jaipur, Rajasthan, India',
            subtitle: 'By appointment only',
            action: '#',
            actionLabel: 'Get Directions',
            highlight: false,
        },
    ];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Nunito+Sans:wght@300;400;500;600;700&display=swap');

                .utsav-contact-layout {
                    display: flex;
                    flex-direction: column;
                    gap: 40px;
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 120px 24px 80px;
                }
                @media (min-width: 768px) {
                    .utsav-contact-layout {
                        flex-direction: row;
                        gap: 60px;
                        padding: 140px 24px 80px;
                    }
                }
                .utsav-contact-info {
                    width: 100%;
                }
                @media (min-width: 768px) {
                    .utsav-contact-info {
                        width: 380px;
                        flex-shrink: 0;
                    }
                }
                .utsav-contact-form {
                    flex: 1;
                }
                .utsav-contact-input:focus {
                    border-color: #C5974B !important;
                }
                .utsav-form-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 16px;
                    margin-bottom: 16px;
                }
                @media (min-width: 480px) {
                    .utsav-form-grid {
                        grid-template-columns: 1fr 1fr;
                    }
                }
            `}</style>

            <div style={{ minHeight: '100vh', backgroundColor: '#FFFDF8' }}>
                <Header cartCount={0} />

                {/* Hero */}
                <div style={{
                    background: 'linear-gradient(135deg, #1B3A2D 0%, #2D5A45 100%)',
                    textAlign: 'center',
                    padding: '120px 24px 60px',
                }}>
                    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                        <div style={{
                            width: '40px',
                            height: '2px',
                            backgroundColor: '#C5974B',
                            margin: '0 auto 16px',
                        }} />
                        <h1 style={{
                            fontFamily: "'Libre Baskerville', Georgia, serif",
                            fontSize: 'clamp(28px, 4vw, 42px)',
                            fontWeight: 700,
                            color: '#FFFFFF',
                            margin: '0 0 12px 0',
                        }}>
                            Get in Touch
                        </h1>
                        <p style={{
                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                            fontSize: '16px',
                            color: 'rgba(255,255,255,0.8)',
                            margin: 0,
                            lineHeight: 1.6,
                        }}>
                            Have questions about custom orders, sizing, or our festive collections? We are here to help.
                        </p>
                    </div>
                </div>

                <div className="utsav-contact-layout">
                    {/* Contact Info Cards */}
                    <div className="utsav-contact-info">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {contactInfo.map((info, idx) => (
                                <a
                                    key={idx}
                                    href={info.action}
                                    target={info.action.startsWith('http') ? '_blank' : undefined}
                                    rel={info.action.startsWith('http') ? 'noopener noreferrer' : undefined}
                                    style={{
                                        textDecoration: 'none',
                                        backgroundColor: info.highlight ? '#1B3A2D' : '#FFFFFF',
                                        borderRadius: '14px',
                                        padding: '24px',
                                        boxShadow: info.highlight
                                            ? '0 8px 30px rgba(27, 58, 45, 0.2)'
                                            : '0 2px 12px rgba(27, 58, 45, 0.06)',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer',
                                        border: info.highlight ? 'none' : '1px solid #E8E0D4',
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                                        <div style={{
                                            width: '44px',
                                            height: '44px',
                                            borderRadius: '10px',
                                            backgroundColor: info.highlight ? 'rgba(197, 151, 75, 0.2)' : '#F8F3EC',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            color: info.highlight ? '#C5974B' : '#1B3A2D',
                                        }}>
                                            {info.icon}
                                        </div>
                                        <div>
                                            <h3 style={{
                                                fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                                fontSize: '14px',
                                                fontWeight: 700,
                                                color: info.highlight ? '#C5974B' : '#1B3A2D',
                                                margin: '0 0 4px 0',
                                            }}>
                                                {info.title}
                                            </h3>
                                            <p style={{
                                                fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                                fontSize: '15px',
                                                fontWeight: 600,
                                                color: info.highlight ? '#FFFFFF' : '#1B3A2D',
                                                margin: '0 0 4px 0',
                                            }}>
                                                {info.detail}
                                            </p>
                                            <p style={{
                                                fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                                fontSize: '12px',
                                                color: info.highlight ? 'rgba(255,255,255,0.7)' : '#5A5A5A',
                                                margin: 0,
                                            }}>
                                                {info.subtitle}
                                            </p>
                                        </div>
                                    </div>
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Contact Form */}
                    <div className="utsav-contact-form">
                        {submitted ? (
                            <div style={{
                                textAlign: 'center',
                                backgroundColor: '#FFFFFF',
                                borderRadius: '16px',
                                padding: '60px 32px',
                                boxShadow: '0 4px 20px rgba(27, 58, 45, 0.08)',
                            }}>
                                <div style={{
                                    width: '64px',
                                    height: '64px',
                                    borderRadius: '50%',
                                    backgroundColor: '#E8F5E9',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 20px',
                                }}>
                                    <Send size={28} color="#1B3A2D" />
                                </div>
                                <h2 style={{
                                    fontFamily: "'Libre Baskerville', Georgia, serif",
                                    fontSize: '24px',
                                    fontWeight: 700,
                                    color: '#1B3A2D',
                                    margin: '0 0 12px 0',
                                }}>
                                    Message Sent!
                                </h2>
                                <p style={{
                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                    fontSize: '15px',
                                    color: '#5A5A5A',
                                    lineHeight: 1.6,
                                    margin: 0,
                                }}>
                                    Thank you for reaching out. Our team will get back to you within 24 hours.
                                    For urgent queries, please WhatsApp us directly.
                                </p>
                            </div>
                        ) : (
                            <div style={{
                                backgroundColor: '#FFFFFF',
                                borderRadius: '16px',
                                padding: '32px',
                                boxShadow: '0 4px 20px rgba(27, 58, 45, 0.08)',
                            }}>
                                <h2 style={{
                                    fontFamily: "'Libre Baskerville', Georgia, serif",
                                    fontSize: '22px',
                                    fontWeight: 700,
                                    color: '#1B3A2D',
                                    margin: '0 0 24px 0',
                                }}>
                                    Send Us a Message
                                </h2>

                                <form onSubmit={handleSubmit}>
                                    <div className="utsav-form-grid">
                                        <div>
                                            <label style={labelStyle}>Full Name</label>
                                            <input
                                                className="utsav-contact-input"
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                style={inputStyle}
                                                placeholder="Your full name"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Email</label>
                                            <input
                                                className="utsav-contact-input"
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                style={inputStyle}
                                                placeholder="your@email.com"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="utsav-form-grid">
                                        <div>
                                            <label style={labelStyle}>Phone</label>
                                            <input
                                                className="utsav-contact-input"
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                style={inputStyle}
                                                placeholder="+91 98765 43210"
                                            />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Enquiry Type</label>
                                            <select
                                                name="orderType"
                                                value={formData.orderType}
                                                onChange={handleChange}
                                                style={{
                                                    ...inputStyle,
                                                    appearance: 'none',
                                                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23C5974B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                                                    backgroundRepeat: 'no-repeat',
                                                    backgroundPosition: 'right 12px center',
                                                }}
                                            >
                                                <option value="general">General Enquiry</option>
                                                <option value="custom">Custom Order / Sizing</option>
                                                <option value="bridal">Bridal Consultation</option>
                                                <option value="order">Order Related</option>
                                                <option value="wholesale">Wholesale / Bulk</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: '16px' }}>
                                        <label style={labelStyle}>Subject</label>
                                        <input
                                            className="utsav-contact-input"
                                            type="text"
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleChange}
                                            style={inputStyle}
                                            placeholder="How can we help?"
                                            required
                                        />
                                    </div>

                                    <div style={{ marginBottom: '24px' }}>
                                        <label style={labelStyle}>Message</label>
                                        <textarea
                                            className="utsav-contact-input"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            style={{
                                                ...inputStyle,
                                                minHeight: '120px',
                                                resize: 'vertical',
                                            }}
                                            placeholder="Tell us about your requirements, occasion details, measurements, etc."
                                            required
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        style={{
                                            width: '100%',
                                            padding: '16px',
                                            backgroundColor: '#C5974B',
                                            color: '#FFFFFF',
                                            border: 'none',
                                            borderRadius: '10px',
                                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                            fontSize: '14px',
                                            fontWeight: 700,
                                            letterSpacing: '1px',
                                            textTransform: 'uppercase',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px',
                                            transition: 'all 0.3s ease',
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#B8873F'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#C5974B'; }}
                                    >
                                        <Send size={16} />
                                        Send Message
                                    </button>
                                </form>
                            </div>
                        )}
                    </div>
                </div>

                <Footer />
            </div>
        </>
    );
};

export default Template23Contact;
